import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { formatNumber } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { LoggerService } from '@services/logger.service';
import { BiggerDiscountService } from '@services/bigger-discount.service';
import { MsDetailModel } from '@/models/tables/ms-detail.model';
import { BiggerDiscountRequestModel } from '@/models/tables/bigger-discount-request.model';
import { BrandModel } from '@/models/tables/brand.model';
import { faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { saveAs } from 'file-saver';

export interface TableBiggerDiscountColumnStructure {
  anno: number;
  codiceMarca: string;
  consegneTotali: number;
  deltaObiettivo: number;
  descrizioneMarca: string;
  tipo: string;
  id: string;
  mese: number;
  obiettivoMese: number;
  percObiettivo: number;
  totalePrezzoAcquistoNetto: number;
  percMsPrevista: number;
  maturatoPrevisto: number;
  importoLiquidato: number;
  dettaglio?: MsDetailModel[];
}

@Component({
  selector: 'app-table-bigger-discount',
  templateUrl: './table-bigger-discount.component.html',
  styleUrls: ['./table-bigger-discount.component.scss'],
})
export class TableBiggerDiscountComponent implements OnInit, OnDestroy {
  @Input() data: object;
  @Input() triggerDate$: Subject<any>;
  @Input() brands: BrandModel[] = [];

  faFileExcel = faFileExcel;
  private subscription: Subscription;
  today = new Date();
  // Non più necessario con tabella HTML standard
  // displayedColumns: string[] = [];
  columns = [
    {
      columnDef: 'marca',
      header: 'Marca',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.descrizioneMarca}`,
    },
    {
      columnDef: 'mese',
      header: 'Mese',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.mese}`,
    },
    {
      columnDef: 'tipo',
      header: 'Tipo vendita',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.tipo}`,
    },
    {
      columnDef: 'obiettivoMese',
      header: 'Obiettivo mese',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.obiettivoMese}`,
    },
    {
      columnDef: 'consegneTotali',
      header: 'Consegne Totali',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.consegneTotali}`,
    },
    {
      columnDef: 'deltaObiettivo',
      header: 'delta Obiettivo',
      cell: (element: TableBiggerDiscountColumnStructure) => `${element.deltaObiettivo}`,
    },
    {
      columnDef: 'percObiettivo',
      header: '% Obiettivo',
      cell: (element: TableBiggerDiscountColumnStructure) =>
        formatNumber(Number(`${element.percObiettivo}`), 'it-IT', '1.2-2') + ' %',
    },
    {
      columnDef: 'totalePrezzoAcquistoNetto',
      header: 'Totale prezzo acquisto netto',
      cell: (element: TableBiggerDiscountColumnStructure) =>
        formatNumber(Number(`${element.totalePrezzoAcquistoNetto}`), 'it-IT', '1.2-2'),
    },
    {
      columnDef: 'percMsPrevista',
      header: '% MS prevista',
      cell: (element: TableBiggerDiscountColumnStructure) =>
        formatNumber(Number(`${element.percMsPrevista}`), 'it-IT', '1.2-2') + ' %',
    },
    {
      columnDef: 'maturatoPrevisto',
      header: 'Maturato previsto',
      cell: (element: TableBiggerDiscountColumnStructure) =>
        formatNumber(Number(`${element.maturatoPrevisto}`), 'it-IT', '1.2-2'),
    },
    {
      columnDef: 'importoLiquidato',
      header: 'Importo liquidato',
      cell: (element: TableBiggerDiscountColumnStructure) =>
        formatNumber(Number(`${element.importoLiquidato}`), 'it-IT', '1.2-2'),
    },
  ];
  dataSource: TableBiggerDiscountColumnStructure[] = [];
  expandedRow: TableBiggerDiscountColumnStructure | null = null;

  detailColumns = [
    {
      columnDef: 'codiceMs',
      header: 'Codice MS',
      cell: (element: MsDetailModel) => `${element.codiceMs}`,
    },
    {
      columnDef: 'descrizioneMs',
      header: 'Descrizione MS',
      cell: (element: MsDetailModel) => `${element.descrizioneMs}`,
    },
    {
      columnDef: 'percMsPrevista',
      header: '% MS Prevista',
      cell: (element: MsDetailModel) =>
        formatNumber(Number(`${element.percMsPrevista}`), 'it-IT', '1.2-2') + ' %',
    },
    {
      columnDef: 'numeroPratiche',
      header: 'Numero Pratiche',
      cell: (element: MsDetailModel) => `${element.numeroPratiche}`,
    },
    {
      columnDef: 'totalePrezzoAcquistoNetto',
      header: 'Totale Prezzo Acquisto Netto',
      cell: (element: MsDetailModel) =>
        formatNumber(Number(`${element.totalePrezzoAcquistoNetto}`), 'it-IT', '1.2-2'),
    },
    {
      columnDef: 'maturatoPrevisto',
      header: 'Maturato Previsto',
      cell: (element: MsDetailModel) =>
        formatNumber(Number(`${element.maturatoPrevisto}`), 'it-IT', '1.2-2'),
    },
    {
      columnDef: 'importoLiquidato',
      header: 'Importo Liquidato',
      cell: (element: MsDetailModel) =>
        formatNumber(Number(`${element.importoLiquidato}`), 'it-IT', '1.2-2'),
    },
  ];

  detailDisplayedColumns: string[] = [
    'codiceMs',
    'descrizioneMs',
    'percMsPrevista',
    'numeroPratiche',
    'totalePrezzoAcquistoNetto',
    'maturatoPrevisto',
    'importoLiquidato',
  ];

  constructor(
    private biggerDiscountService: BiggerDiscountService,
    protected toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.subscription = this.triggerDate$.subscribe({
      next: (objectDate) => {
        LoggerService.log('UPDATE TRIGGER TABLE', objectDate);

        const quarterValue = objectDate.quarter || 4;
        const quarterRoman = ['I', 'II', 'III', 'IV'][quarterValue - 1];

        const dashboardRequest = new BiggerDiscountRequestModel();
        dashboardRequest.anno = objectDate.year;
        dashboardRequest.trimestre = quarterRoman;
        dashboardRequest.mese = objectDate.month;
        dashboardRequest.marcaId = objectDate.brandId;

        this.biggerDiscountService.getDashboard(dashboardRequest).subscribe({
          next: (data) => {
            this.dataSource = data;
          },
          error: (error) => {
            LoggerService.error(error);
            this.toastr.error(error);
          },
          complete: () => {
            LoggerService.log('Request done');
          },
        });
      },
    });
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  onRowClick(row: TableBiggerDiscountColumnStructure): void {
    // Toggle expansion for the clicked row
    this.expandedRow = this.expandedRow === row ? null : row;
  }

  hasDetails(row: TableBiggerDiscountColumnStructure): boolean {
    return row.dettaglio !== undefined && row.dettaglio !== null && row.dettaglio.length > 0;
  }

  calculateTotals(dettagli: MsDetailModel[]): {
    totaleNumeroPratiche: number;
    totalePrezzoAcquistoNetto: number;
    totaleMaturatoPrevisto: number;
    totaleImportoLiquidato: number;
  } {
    return {
      totaleNumeroPratiche: dettagli.reduce((sum, det) => sum + (det.numeroPratiche || 0), 0),
      totalePrezzoAcquistoNetto: dettagli.reduce(
        (sum, det) => sum + (det.totalePrezzoAcquistoNetto || 0),
        0,
      ),
      totaleMaturatoPrevisto: dettagli.reduce((sum, det) => sum + (det.maturatoPrevisto || 0), 0),
      totaleImportoLiquidato: dettagli.reduce((sum, det) => sum + (det.importoLiquidato || 0), 0),
    };
  }

  formatNumberValue(value: number): string {
    return formatNumber(value, 'it-IT', '1.2-2');
  }

  downloadExcel(row: TableBiggerDiscountColumnStructure): void {
    let marcaId: number | null = null;

    if (row.dettaglio && row.dettaglio.length > 0) {
      marcaId = row.dettaglio[0].marcaId;
    } else if (this.brands && this.brands.length > 0) {
      const brand = this.brands.find((b) => b.codiceMarca === row.codiceMarca);
      if (brand) {
        marcaId = brand.id;
      }
    }

    if (marcaId === null) {
      this.toastr.error("Impossibile recuperare la marca per l'export");
      return;
    }

    const request = {
      marcaId: marcaId,
      anno: row.anno,
      mese: row.mese,
      tipo: row.tipo,
    };

    this.biggerDiscountService.exportSimulatorXlsx(request).subscribe({
      next: (blob) => {
        const filename = `Export-simulator-${row.descrizioneMarca}-${row.mese}-${row.anno}-${row.tipo}.xlsx`;
        saveAs(blob, filename);
      },
      error: (error) => {
        LoggerService.error(error);
        this.toastr.error('Errore durante il download del file Excel');
      },
    });
  }
}
